import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Modal, Input, Form, message, Tooltip } from 'antd'
import { CloseOutlined } from '@ant-design/icons'

import Box from '~/core/Box'
import Text from '~/core/Text'
import logger from '~/lib/logger'
import { getUserIdentity } from '~/redux-modules/identity'
import { ADMIN_PATH } from '~/redux-modules/preferences'
import LearnMoreLink from '~/core/LearnMoreLink/LearnMoreLink'
import getColor from '~/lib/getColor'
import { UserTeamsDocument, useCreateTeamMutation } from '~/gql/graphql'

import {
    TEAM_NAME_ERROR,
    TEAM_NAME_FIELD,
    TEAM_NAME_TITLE,
    TEAM_NAME_PLACEHOLDER,
    NOTE_TEXT,
    CREATE_TEAM_DESCRIPTION,
    CREATE_TEAM_TITLE,
    CREATE_BUTTON_TEXT,
    TEAM_NAME_ERROR_MSG,
    CREATE_TEAM_FORM_NAME,
    TEAM_NAME_FORM_RULES,
    LEARN_MORE_TEXT,
    LINK_TO_LEARN_ABOUT_TEAMS_PERMISSIONS,
    IS_NEW_TEAM_SEARCH_PARM,
} from './constants'
import ConfirmCloseCreateTeamModal from './ConfirmCloseCreateTeamModal'

type CreateTeamModalPropsType = {
    visible: boolean
    onCancel: () => void
    userId?: number
    businessAccountMembershipId?: string
}

const CreateTeamModal = (props: CreateTeamModalPropsType) => {
    const { visible, onCancel, userId, businessAccountMembershipId } = props

    const navigate = useNavigate()
    const [form] = Form.useForm()
    const { id, businessAccountMembership, isAdmin } =
        useSelector(getUserIdentity)
    const businessAccountId = businessAccountMembership?.businessAccount?.id

    // const [createTeam] = useMutation<CreateTeamType>(createTeamMutation)
    const [createTeam] = useCreateTeamMutation()
    const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(true)
    const [okButtonLoading, setOkButtonLoading] = useState<boolean>(false)
    const [showConfirmTooltip, setShowConfirmTooltip] = useState<boolean>(false)

    const onFormUpdated = () => {
        setShowConfirmTooltip(false)
        const hasErrors = form.getFieldError(TEAM_NAME_FIELD).length > 0

        setOkButtonDisabled(hasErrors)
    }

    const onCreateTeam = async () => {
        setOkButtonLoading(true)
        const teamName = form.getFieldValue(TEAM_NAME_FIELD)
        const variables = {
            businessAccountId: businessAccountMembershipId || businessAccountId,
            name: teamName,
            userId: userId || id,
        }

        try {
            const results = await createTeam({
                variables,
                refetchQueries: [UserTeamsDocument],
            })

            if (!results?.data?.createTeam) {
                const errMsg = 'Failed to create workspace'

                message.error(errMsg)
                throw new Error(errMsg)
            }

            const teamId = results?.data?.createTeam?.team?.id

            const newTeamPath =
                userId && isAdmin
                    ? `${ADMIN_PATH}/user/${userId}/team/${teamId}?${IS_NEW_TEAM_SEARCH_PARM}=true`
                    : `/pages/team/${teamId}?${IS_NEW_TEAM_SEARCH_PARM}=true`

            navigate(newTeamPath)

            onCancel()
        } catch (err: unknown) {
            logger.exception(err, {
                extra: {
                    info: 'CreateNewTeamModal::Failed to create workspace: ',
                    variables,
                },
            })
            const errMessage = (err as { [key: string]: string })?.message

            if (errMessage && errMessage === TEAM_NAME_ERROR_MSG) {
                form.setFields([
                    { name: TEAM_NAME_FIELD, errors: [TEAM_NAME_ERROR] },
                ])
            }

            setOkButtonLoading(false)
        }
    }

    function onCloseModal() {
        if (showConfirmTooltip === true) {
            // delegate action to close modal to the confirm tooltip
            return
        }

        const teamName = form.getFieldValue(TEAM_NAME_FIELD)

        if (teamName) {
            setShowConfirmTooltip(true)
        } else {
            onCancel()
        }
    }

    return (
        <Modal
            title={CREATE_TEAM_TITLE}
            okText={CREATE_BUTTON_TEXT}
            okButtonProps={{
                disabled: okButtonDisabled,
                loading: okButtonLoading,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Adding a data test target to default btn
                'data-test-target': 'create-team-action-btn',
            }}
            cancelButtonProps={{
                style: { display: 'none' },
            }}
            visible={visible}
            onCancel={onCloseModal}
            onOk={onCreateTeam}
            width="550px"
            centered
            maskClosable={true}
            closeIcon={
                <Tooltip
                    title={
                        <ConfirmCloseCreateTeamModal
                            onNo={() => {
                                setShowConfirmTooltip(false)
                            }}
                            onYes={() => {
                                setShowConfirmTooltip(false)
                                onCancel()
                            }}
                        />
                    }
                    overlayInnerStyle={{
                        color: getColor('blackAlpha85'),
                        padding: '16px',
                    }}
                    overlayStyle={{ maxWidth: '320px' }}
                    placement="bottom"
                    color="white"
                    trigger="click"
                    visible={showConfirmTooltip}
                >
                    <CloseOutlined />
                </Tooltip>
            }
        >
            <Form
                name={CREATE_TEAM_FORM_NAME}
                form={form}
                layout="vertical"
                onFieldsChange={onFormUpdated}
            >
                <Text
                    fontWeight={400}
                    style={{ display: 'block', marginBottom: '24px' }}
                >
                    {CREATE_TEAM_DESCRIPTION}
                </Text>
                <Form.Item
                    name={TEAM_NAME_FIELD}
                    rules={TEAM_NAME_FORM_RULES}
                    label={TEAM_NAME_TITLE}
                    validateTrigger={['onBlur', 'onChange']}
                >
                    <Input
                        autoFocus
                        name={TEAM_NAME_FIELD}
                        data-test-target={TEAM_NAME_FIELD}
                        placeholder={TEAM_NAME_PLACEHOLDER}
                    />
                </Form.Item>
                <Box marginTop="26px">
                    <Text color="blackAlpha45">{NOTE_TEXT}</Text>
                </Box>
                <Box marginTop="26px">
                    <LearnMoreLink
                        linkTo={LINK_TO_LEARN_ABOUT_TEAMS_PERMISSIONS}
                        followingText={LEARN_MORE_TEXT}
                    />
                </Box>
            </Form>
        </Modal>
    )
}

export default CreateTeamModal
